import { createFileRoute } from "@tanstack/react-router";
import {
  Accordion,
  AccordionItem,
  SidebarPage,
  Loader,
  AppError,
  Card,
  EmptyState,
  Heading,
  Tag,
  Button,
  toast,
} from "@bfc-software/pallet";
import {
  DialogTrigger,
  ModalOverlay,
  Modal,
  Dialog,
} from "react-aria-components";
import { useCallback, useEffect, useRef, useState } from "react";
import { useFtpFetchClient } from "@bfc-software/bfc-web-ui/lib/ftp-api-client";
import { DateValue } from "@bfc-software/bfc-web-ui/ui/shared/date-value";
import modalStyles from "@bfc-software/pallet/components/structure/modal.module.css";
import clsx from "clsx";
import YAML from "yaml";

import styles from "./ftp-config.module.css";

// ============================================================================
// Route
// ============================================================================

export const Route = createFileRoute(
  "/$lang/_layout/orgs/$org/_layout/internal/whs/$location/_layout/ftp-config",
)({
  staticData: {
    crumb: () => ({ label: "FTP Config" }),
  },
  component: RouteComponent,
});

// ============================================================================
// Types
// ============================================================================

type EditorTab = "visual" | "yaml";

type Obj = Record<string, unknown>;

type PageState =
  | { kind: "loading" }
  | { kind: "not-found" }
  | { kind: "failed" }
  | {
      kind: "editing";
      customerId: string;
      config: Obj;
      originalYaml: string;
      yaml: string;
      lastModified?: string;
      saving: boolean;
      tab: EditorTab;
    }
  | { kind: "creating" };

// ============================================================================
// Visual Config View - read-only, focused on mappings & transforms
// ============================================================================

/** Inline key-value display */
function Prop({
  label,
  children,
}: {
  label: string;
  children?: React.ReactNode;
}) {
  return (
    <span className={styles.prop}>
      <span className={styles.propLabel}>{label}:</span>{" "}
      <span className={styles.propValue}>{children ?? "—"}</span>
    </span>
  );
}

/** HTTP method + URL badge */
function EndpointBadge({ endpoint }: { endpoint: Obj }) {
  const method = String(endpoint["method"] ?? "GET").toUpperCase();
  const base = String(endpoint["base_url"] ?? "");
  const path = String(endpoint["path"] ?? "");
  const cls =
    method === "POST"
      ? styles.methodPost
      : method === "PUT"
        ? styles.methodPut
        : method === "DELETE"
          ? styles.methodDelete
          : styles.methodGet;
  return (
    <span className={styles.endpointBadge}>
      <span className={clsx(styles.methodBadge, cls)}>{method}</span>
      <span>
        {base}
        {path}
      </span>
    </span>
  );
}

/** Mapping table (key → value) with optional expand */
function MappingTable({
  entries,
  targetLabel,
  sourceLabel,
}: {
  entries: [string, string][];
  targetLabel?: string;
  sourceLabel?: string;
}) {
  const PREVIEW = 8;
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? entries : entries.slice(0, PREVIEW);
  const hasMore = entries.length > PREVIEW;

  if (entries.length === 0)
    return <p className={styles.muted}>No field mappings defined.</p>;

  return (
    <div>
      <table className={styles.mappingTable}>
        <thead>
          <tr>
            <th>{targetLabel ?? "Target Field"}</th>
            <th>{sourceLabel ?? "Source"}</th>
          </tr>
        </thead>
        <tbody>
          {visible.map(([target, source]) => (
            <tr key={target}>
              <td>{target}</td>
              <td>{source}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {hasMore && (
        <Button
          kind="text-only"
          text={expanded ? "Show less" : `Show all ${entries.length}`}
          variant="tertiary"
          scale="inline"
          onPress={() => setExpanded((v) => !v)}
        />
      )}
    </div>
  );
}

/** Transform pipeline display */
function TransformTable({
  transforms,
}: {
  transforms: Record<string, unknown[]>;
}) {
  const entries = Object.entries(transforms).filter(
    ([, v]) => Array.isArray(v) && v.length > 0,
  );
  if (entries.length === 0) return null;

  return (
    <table className={styles.mappingTable}>
      <thead>
        <tr>
          <th>Field</th>
          <th>Pipeline</th>
        </tr>
      </thead>
      <tbody>
        {entries.map(([field, txList]) => (
          <tr key={field}>
            <td>{field}</td>
            <td className={styles.transformPipeline}>
              {(txList as Obj[]).map((t, i) => {
                const type = String(t["type"] ?? "?");
                const params = Object.entries(t)
                  .filter(([k]) => k !== "type")
                  .map(([k, v]) => `${k}=${JSON.stringify(v)}`)
                  .join(", ");
                return (
                  <span key={i} className={styles.transformStep}>
                    {type}
                    {params ? `(${params})` : ""}
                  </span>
                );
              })}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function toMappingEntries(mappings: Obj | undefined): [string, string][] {
  if (!mappings) return [];
  return Object.entries(mappings).map(([k, v]) => [
    k,
    typeof v === "string" ? v : JSON.stringify(v),
  ]);
}

// ============================================================================
// File type visual view
// ============================================================================

function isMultiSection(ft: Obj): boolean {
  const t = String((ft["input_format"] as Obj | undefined)?.["type"] ?? "");
  return t === "multi_section_tsv" || t === "multi_section_csv";
}

function FileTypeView({ name, ft }: { name: string; ft: Obj }) {
  const endpoint = ft["endpoint"] as Obj | undefined;
  const auth = ft["auth"] as Obj | undefined;
  const inputFmt = ft["input_format"] as Obj | undefined;
  const fieldMappings = ft["field_mappings"] as Obj | undefined;
  const transforms = ft["transformations"] as
    Record<string, unknown[]> | undefined;
  const validation = ft["validation"] as Obj | undefined;
  const multiSection = isMultiSection(ft);

  const endpointSummary = endpoint
    ? `${String(endpoint["method"] ?? "").toUpperCase()} ${String(endpoint["path"] ?? "")}`
    : "";

  return (
    <AccordionItem
      label={`${name}${endpointSummary ? ` — ${endpointSummary}` : ""}`}
      initiallyOpen
    >
      <div className={styles.typeBody}>
        {/* Summary line */}
        <div className={styles.typeSummaryRow}>
          {endpoint && <EndpointBadge endpoint={endpoint} />}
          <div className={styles.tagsRow}>
            {inputFmt && (
              <Tag
                label={String(inputFmt["type"] ?? "json")}
                display={{ tone: "info" }}
              />
            )}
            {auth && (
              <Tag
                label={`Auth: ${String(auth["type"] ?? "none")}`}
                display={{ tone: "auto" }}
              />
            )}
            {ft["dry_run"] === true && (
              <Tag label="Dry Run" display={{ tone: "warning" }} />
            )}
          </div>
        </div>

        <div className={styles.metaRow}>
          <Prop label="Pattern">
            <code>{String(ft["filename_pattern"] ?? "")}</code>
          </Prop>
          {ft["batch"] != null && (
            <Prop label="Batch">
              {String((ft["batch"] as Obj)["size"] ?? 100)}
              {(ft["batch"] as Obj)["parallel"] === true ? ", parallel" : ""}
            </Prop>
          )}
          {ft["error_handling"] != null && (
            <Prop label="On failure">
              {String((ft["error_handling"] as Obj)["on_failure"] ?? "abort")}
              {" ("}
              {String((ft["error_handling"] as Obj)["retry_count"] ?? 3)}{" "}
              retries)
            </Prop>
          )}
        </div>

        {/* Multi-section: sections + record structure */}
        {multiSection && <MultiSectionView ft={ft} />}

        {/* Simple format: top-level mappings + transforms */}
        {!multiSection && (
          <>
            {fieldMappings && Object.keys(fieldMappings).length > 0 && (
              <div className={styles.subSection}>
                <Heading
                  level="3"
                  text={`Field Mappings (${Object.keys(fieldMappings).length})`}
                />
                <MappingTable entries={toMappingEntries(fieldMappings)} />
              </div>
            )}

            {transforms && <TransformSection transforms={transforms} />}
          </>
        )}

        {/* Validation */}
        {validation &&
          Array.isArray(validation["custom_rules"]) &&
          (validation["custom_rules"] as Obj[]).length > 0 && (
            <div className={styles.subSection}>
              <Heading level="3" text="Validation" />
              <div className={styles.tagsRow}>
                {(validation["custom_rules"] as Obj[]).map(
                  (rule: Obj, i: number) => (
                    <Tag
                      key={i}
                      label={`${String(rule["rule"])}: ${String(rule["message"])}`}
                      display={{ tone: "auto" }}
                    />
                  ),
                )}
              </div>
            </div>
          )}
      </div>
    </AccordionItem>
  );
}

function TransformSection({
  transforms,
  heading,
}: {
  transforms: Record<string, unknown[]>;
  heading?: string;
}) {
  const count = Object.keys(transforms).filter(
    (k) => Array.isArray(transforms[k]) && transforms[k].length > 0,
  ).length;
  if (count === 0) return null;
  return (
    <div className={styles.subSection}>
      <Heading
        level="3"
        text={heading ?? `Transformations (${count} fields)`}
      />
      <TransformTable transforms={transforms} />
    </div>
  );
}

// ============================================================================
// Multi-section view
// ============================================================================

function MultiSectionView({ ft }: { ft: Obj }) {
  const inputFmt = (ft["input_format"] ?? {}) as Obj;
  const sections = (inputFmt["sections"] ?? {}) as Record<string, Obj>;
  const recordStructure = ft["record_structure"] as Obj | undefined;
  const children = (recordStructure?.["children"] ?? {}) as Record<string, Obj>;

  return (
    <>
      {/* Sections overview */}
      <div className={styles.subSection}>
        <Heading
          level="3"
          text={`Sections (${Object.keys(sections).length})`}
        />
        <div className={styles.sectionCards}>
          {Object.entries(sections).map(([sectionName, section]) => {
            const id = (section["identifier"] ?? {}) as Obj;
            const idParts: string[] = [];
            if (id["column_count"] != null)
              idParts.push(`column_count = ${id["column_count"]}`);
            if (id["first_column_pattern"] != null)
              idParts.push(`pattern = ${String(id["first_column_pattern"])}`);
            if (id["line_range"] != null)
              idParts.push(`lines = ${JSON.stringify(id["line_range"])}`);

            return (
              <div key={sectionName} className={styles.sectionCard}>
                <div className={styles.sectionCardHeader}>
                  <strong>{sectionName}</strong>
                  <div className={styles.tagsRow}>
                    <Tag
                      label={
                        section["has_header"] === false
                          ? "No header"
                          : "Has header"
                      }
                      display={{ tone: "auto" }}
                    />
                    {section["header_transform"] != null && (
                      <Tag
                        label={String(section["header_transform"])}
                        display={{ tone: "auto" }}
                      />
                    )}
                  </div>
                </div>
                {idParts.length > 0 && (
                  <div className={styles.sectionCardDetail}>
                    Identified by: {idParts.join(", ")}
                  </div>
                )}
                {Array.isArray(section["columns"]) && (
                  <div className={styles.sectionCardDetail}>
                    <code>{(section["columns"] as string[]).join(", ")}</code>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Record structure */}
      {recordStructure && (
        <div className={styles.subSection}>
          <Heading level="3" text="Record Structure" />
          <div className={styles.joinDiagram}>
            <div className={styles.joinRoot}>
              Root: <strong>{String(recordStructure["root"])}</strong>
            </div>
            {Object.entries(children).map(([childName, child]) => (
              <div key={childName} className={styles.joinChild}>
                <span className={styles.joinArrow}>└─</span>
                <strong>{childName}</strong>
                <span className={styles.joinDetail}>
                  join <code>{String(child["from_section"])}</code> on{" "}
                  <code>{String(child["join_key"])}</code> → field{" "}
                  <code>{String(child["target_field"])}</code>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top-level (root section) field mappings */}
      {ft["field_mappings"] != null &&
        Object.keys(ft["field_mappings"] as Obj).length > 0 && (
          <div className={styles.subSection}>
            <Heading
              level="3"
              text={`Field Mappings — root (${Object.keys(ft["field_mappings"] as Obj).length})`}
            />
            <MappingTable
              entries={toMappingEntries(ft["field_mappings"] as Obj)}
            />
          </div>
        )}

      {ft["transformations"] != null && (
        <TransformSection
          transforms={ft["transformations"] as Record<string, unknown[]>}
          heading="Transformations — root"
        />
      )}

      {/* Per-child mappings & transforms */}
      {Object.entries(children).map(([childName, child]) => {
        const childMappings = child["field_mappings"] as Obj | undefined;
        const childTransforms = child["transformations"] as
          Record<string, unknown[]> | undefined;
        const hasMappings =
          childMappings && Object.keys(childMappings).length > 0;
        const hasTransforms =
          childTransforms &&
          Object.keys(childTransforms).some(
            (k) =>
              Array.isArray(childTransforms[k]) &&
              childTransforms[k].length > 0,
          );

        if (!hasMappings && !hasTransforms) return null;

        return (
          <div key={childName}>
            {hasMappings && (
              <div className={styles.subSection}>
                <Heading
                  level="3"
                  text={`Field Mappings — ${childName} (${Object.keys(childMappings).length})`}
                />
                <MappingTable entries={toMappingEntries(childMappings)} />
              </div>
            )}
            {hasTransforms && (
              <TransformSection
                transforms={childTransforms}
                heading={`Transformations — ${childName}`}
              />
            )}
          </div>
        );
      })}
    </>
  );
}

// ============================================================================
// Export type visual view
// ============================================================================

function ExportTypeView({ name, et }: { name: string; et: Obj }) {
  const source = et["source"] as Obj | undefined;
  const output = et["output"] as Obj | undefined;
  const fieldMappings = et["field_mappings"] as Obj | undefined;
  const transforms = et["transformations"] as
    Record<string, unknown[]> | undefined;

  const srcEndpoint = source?.["endpoint"] as Obj | undefined;
  const summary = output
    ? `${String(output["format"] ?? "")} → ${String(output["filename_pattern"] ?? "")}`
    : "";

  return (
    <AccordionItem
      label={`${name}${summary ? ` — ${summary}` : ""}`}
      initiallyOpen
    >
      <div className={styles.typeBody}>
        <div className={styles.typeSummaryRow}>
          {srcEndpoint && <EndpointBadge endpoint={srcEndpoint} />}
          <div className={styles.tagsRow}>
            {output && (
              <Tag
                label={`Output: ${String(output["format"] ?? "?")}`}
                display={{ tone: "info" }}
              />
            )}
            {et["dry_run"] === true && (
              <Tag label="Dry Run" display={{ tone: "warning" }} />
            )}
          </div>
        </div>

        {output && (
          <div className={styles.metaRow}>
            <Prop label="Output pattern">
              <code>{String(output["filename_pattern"] ?? "")}</code>
            </Prop>
          </div>
        )}

        {fieldMappings && Object.keys(fieldMappings).length > 0 && (
          <div className={styles.subSection}>
            <Heading
              level="3"
              text={`Field Mappings (${Object.keys(fieldMappings).length})`}
            />
            <MappingTable
              entries={toMappingEntries(fieldMappings)}
              sourceLabel="Source (API)"
              targetLabel="Output Field"
            />
          </div>
        )}

        {transforms && <TransformSection transforms={transforms} />}
      </div>
    </AccordionItem>
  );
}

// ============================================================================
// Full visual config view
// ============================================================================

function VisualConfigView({ config }: { config: Obj }) {
  const fileTypes = (config["file_types"] ?? {}) as Record<string, Obj>;
  const exportTypes = (config["export_types"] ?? {}) as Record<string, Obj>;
  const settings = (config["settings"] ?? {}) as Obj;
  const ftKeys = Object.keys(fileTypes);
  const etKeys = Object.keys(exportTypes);

  return (
    <div className={styles.visualView}>
      {/* General summary */}
      <Card
        layout="body-only"
        body={
          <div className={styles.configSummaryRow}>
            <Heading level="2" text={String(config["customer_id"] ?? "—")} />
            <div className={styles.tagsRow}>
              <Tag
                label={`v${String(config["version"] ?? "?")}`}
                display={{ tone: "auto" }}
              />
              {settings["timezone"] != null && (
                <Tag
                  label={String(settings["timezone"])}
                  display={{ tone: "auto" }}
                />
              )}
              {settings["log_level"] != null && (
                <Tag
                  label={String(settings["log_level"])}
                  display={{ tone: "auto" }}
                />
              )}
            </div>
          </div>
        }
      />

      {/* File Types */}
      {ftKeys.length > 0 && (
        <Card
          layout="body-only"
          body={
            <>
              <div className={styles.cardHeading}>
                <Heading level="2" text="File Types" />
                <Tag label={String(ftKeys.length)} display={{ tone: "auto" }} />
              </div>
              <Accordion>
                {ftKeys.map((name) => (
                  <FileTypeView key={name} name={name} ft={fileTypes[name]!} />
                ))}
              </Accordion>
            </>
          }
        />
      )}

      {/* Export Types */}
      {etKeys.length > 0 && (
        <Card
          layout="body-only"
          body={
            <>
              <div className={styles.cardHeading}>
                <Heading level="2" text="Export Types" />
                <Tag label={String(etKeys.length)} display={{ tone: "auto" }} />
              </div>
              <Accordion>
                {etKeys.map((name) => (
                  <ExportTypeView
                    key={name}
                    name={name}
                    et={exportTypes[name]!}
                  />
                ))}
              </Accordion>
            </>
          }
        />
      )}
    </div>
  );
}

// ============================================================================
// Editor View (tabs: Visual + YAML)
// ============================================================================

function ConfigEditorView({
  ev,
  customerId,
  onYamlChange,
  onSave,
  onDelete,
  onTabChange,
}: {
  ev: Extract<PageState, { kind: "editing" }>;
  customerId: string;
  onYamlChange: (yaml: string) => void;
  onSave: () => void;
  onDelete: () => void;
  onTabChange: (tab: EditorTab) => void;
}) {
  const hasChanges = ev.yaml !== ev.originalYaml;

  return (
    <div className={styles.editorPanel}>
      {/* Header */}
      <div className={styles.editorHeader}>
        <div className={styles.editorHeaderLeft}>
          <Heading level="2" text={`FTP Config: ${customerId}`} />
          {ev.lastModified && (
            <span className={styles.editorMeta}>
              Last modified: <DateValue dateValue={ev.lastModified} />
            </span>
          )}
        </div>
        <div className={styles.editorHeaderActions}>
          <DeleteConfigButton customerId={customerId} onConfirm={onDelete} />
          <Button
            kind="text-icon-left"
            text={ev.saving ? "Saving…" : "Save"}
            variant="primary"
            icon="Save"
            isDisabled={!hasChanges || ev.saving}
            onPress={onSave}
          />
        </div>
      </div>

      {/* Tab bar */}
      <div className={styles.tabBar}>
        <button
          className={clsx(styles.tab, ev.tab === "visual" && styles.tabActive)}
          onClick={() => onTabChange("visual")}
        >
          Visual
        </button>
        <button
          className={clsx(styles.tab, ev.tab === "yaml" && styles.tabActive)}
          onClick={() => onTabChange("yaml")}
        >
          YAML
        </button>
      </div>

      {/* Tab content */}
      {ev.tab === "visual" ? (
        <VisualConfigView config={ev.config} />
      ) : (
        <textarea
          className={styles.yamlEditor}
          value={ev.yaml}
          onChange={(e) => onYamlChange(e.target.value)}
          spellCheck={false}
        />
      )}
    </div>
  );
}

// ============================================================================
// Create Config View
// ============================================================================

function CreateConfigView({ onCreated }: { onCreated: () => void }) {
  const [yaml, setYaml] = useState(DEFAULT_CONFIG_YAML);
  const [saving, setSaving] = useState(false);
  const client = useFtpFetchClient();

  const handleCreate = useCallback(async () => {
    setSaving(true);
    try {
      const { error: apiError } = await client.POST("/configs", {
        body: { yaml },
      });
      if (apiError) {
        toast.error(
          (apiError as { error?: string }).error ?? "Failed to create config",
        );
        setSaving(false);
        return;
      }
      toast.success("Config created successfully");
      onCreated();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to create config",
      );
      setSaving(false);
    }
  }, [client, yaml, onCreated]);

  return (
    <div className={styles.editorPanel}>
      <div className={styles.editorHeader}>
        <div className={styles.editorHeaderLeft}>
          <Heading level="2" text="New FTP Config" />
        </div>
        <div className={styles.editorHeaderActions}>
          <Button
            kind="text-icon-left"
            text={saving ? "Creating…" : "Create"}
            variant="primary"
            icon="Save"
            isDisabled={saving}
            onPress={handleCreate}
          />
        </div>
      </div>

      <div className={styles.formField}>
        <Heading level="3" text="Config (YAML)" />
        <textarea
          className={styles.yamlEditor}
          value={yaml}
          onChange={(e) => setYaml(e.target.value)}
          spellCheck={false}
        />
      </div>
    </div>
  );
}

const DEFAULT_CONFIG_YAML = `version: "1.0"

file_types:
  item_import:
    filename_pattern: "^item_import.*\\\\.json$"
    endpoint:
      base_url: "https://api.bfc.software"
      path: "/v1/import/items"
      method: "POST"
    auth:
      type: "api_key"
      header_name: "X-API-KEY-DKI"
      secret_arn: ""
    field_mappings: {}
    transformations: {}
    batch:
      size: 100
      parallel: false
    error_handling:
      retry_count: 3
      on_failure: "continue"

settings:
  timezone: "America/New_York"
  log_level: "INFO"
  status_retention_days: 90
`;

// ============================================================================
// Delete Confirm
// ============================================================================

function DeleteConfigButton({
  customerId,
  onConfirm,
}: {
  customerId: string;
  onConfirm: () => void;
}) {
  return (
    <DialogTrigger>
      <Button
        kind="text-icon-left"
        text="Delete"
        variant="danger"
        icon="Trash"
      />
      <ModalOverlay className={modalStyles.modalOverlay} isDismissable>
        <Modal className={modalStyles.modal}>
          <Dialog className={modalStyles.dialog}>
            <div className={styles.deleteConfirm}>
              <p className={styles.deleteConfirmText}>
                Are you sure you want to delete the config for{" "}
                <span className={styles.deleteConfirmId}>{customerId}</span>?
                This action cannot be undone.
              </p>
            </div>
            <div className={styles.modalActions}>
              <Button
                kind="text-only"
                text="Cancel"
                variant="tertiary"
                slot="close"
              />
              <Button
                kind="text-only"
                text="Delete"
                variant="danger"
                onPress={() => onConfirm()}
              />
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </DialogTrigger>
  );
}

// ============================================================================
// Route Component
// ============================================================================

function RouteComponent() {
  const [state, setState] = useState<PageState>({ kind: "loading" });
  const stateRef = useRef(state);
  stateRef.current = state;
  const fetchApi = useFtpFetchClient();

  // Load config for the authenticated customer (server determines customer from JWT)
  const loadConfig = useCallback(async () => {
    setState({ kind: "loading" });
    try {
      const { data, error } = await fetchApi.GET("/configs", {});
      if (error || !data) {
        // 404 or missing → no config yet
        setState({ kind: "not-found" });
        return;
      }
      const detail = data.data;
      setState({
        kind: "editing",
        customerId: detail.customer_id,
        config: detail.config,
        originalYaml: detail.yaml,
        yaml: detail.yaml,
        lastModified: detail.last_modified,
        saving: false,
        tab: "visual",
      });
    } catch {
      setState({ kind: "not-found" });
    }
  }, [fetchApi]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleTabChange = useCallback((tab: EditorTab) => {
    setState((prev) => {
      if (prev.kind !== "editing") return prev;
      if (tab === "visual" && prev.tab === "yaml") {
        try {
          const parsed = YAML.parse(prev.yaml);
          if (parsed && typeof parsed === "object") {
            return { ...prev, tab, config: parsed as Obj };
          }
          toast.error("Invalid YAML: expected an object at root");
          return prev;
        } catch (e) {
          toast.error(
            `YAML parse error: ${e instanceof Error ? e.message : "unknown"}`,
          );
          return prev;
        }
      }
      return { ...prev, tab };
    });
  }, []);

  const handleYamlChange = useCallback((yaml: string) => {
    setState((prev) => {
      if (prev.kind !== "editing") return prev;
      return { ...prev, yaml };
    });
  }, []);

  const handleSave = useCallback(async () => {
    const current = stateRef.current;
    if (current.kind !== "editing") return;

    setState((prev) => {
      if (prev.kind !== "editing") return prev;
      return { ...prev, saving: true };
    });

    try {
      const { data, error } = await fetchApi.PUT("/configs/{customerId}", {
        params: { path: { customerId: current.customerId } },
        body: { yaml: current.yaml },
      });

      if (error) {
        const msg =
          (error as { error?: string }).error ?? "Failed to save config";
        toast.error(msg);
        setState((prev) => {
          if (prev.kind !== "editing") return prev;
          return { ...prev, saving: false };
        });
        return;
      }

      if (data) {
        toast.success("Config saved successfully");
        setState((prev) => {
          if (prev.kind !== "editing") return prev;
          return {
            ...prev,
            saving: false,
            config: data.data.config,
            originalYaml: data.data.yaml,
            yaml: data.data.yaml,
            lastModified: data.data.last_modified,
          };
        });
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save config");
      setState((prev) => {
        if (prev.kind !== "editing") return prev;
        return { ...prev, saving: false };
      });
    }
  }, [fetchApi]);

  const handleDelete = useCallback(async () => {
    const current = stateRef.current;
    if (current.kind !== "editing") return;

    try {
      await fetchApi.DELETE("/configs/{customerId}", {
        params: { path: { customerId: current.customerId } },
      });
      toast.success(`Deleted config for ${current.customerId}`);
      setState({ kind: "not-found" });
    } catch {
      toast.error("Failed to delete config");
    }
  }, [fetchApi]);

  const handleCreateNew = useCallback(() => {
    setState({ kind: "creating" });
  }, []);

  const handleCreated = useCallback(() => {
    loadConfig();
  }, [loadConfig]);

  let content: React.ReactNode;

  switch (state.kind) {
    case "loading":
      content = (
        <div className={styles.loadingContainer}>
          <Loader type="page" />
        </div>
      );
      break;
    case "not-found":
      content = (
        <div className={styles.listPanel}>
          <EmptyState heading="No FTP config found">
            <p>This location does not have an FTP configuration yet.</p>
            <div className={styles.emptyActions}>
              <Button
                kind="text-icon-left"
                text="Create Config"
                variant="primary"
                icon="Plus"
                onPress={handleCreateNew}
              />
            </div>
          </EmptyState>
        </div>
      );
      break;
    case "failed":
      content = (
        <div className={styles.errorContainer}>
          <AppError
            description="Failed to load config"
            resolution="Check your network connection and try again."
          />
        </div>
      );
      break;
    case "editing":
      content = (
        <ConfigEditorView
          ev={state}
          customerId={state.customerId}
          onYamlChange={handleYamlChange}
          onSave={handleSave}
          onDelete={handleDelete}
          onTabChange={handleTabChange}
        />
      );
      break;
    case "creating":
      content = <CreateConfigView onCreated={handleCreated} />;
      break;
  }

  return (
    <SidebarPage sidebar={<div />}>
      <div className="bg-surface h-full w-full">{content}</div>
    </SidebarPage>
  );
}
